/**
 * Authentication Context and Provider
 *
 * Provides authentication state and methods to the entire application
 * through React Context.
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { AuthContextType, AuthState } from '../types';
import {
  extractTenantId,
  signIn as authSignIn,
  signUp as authSignUp,
  signOut as authSignOut,
  resetPassword as authResetPassword,
  onAuthStateChange
} from '../services/authService';

// Create the authentication context with default values
const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  userId: null,
  tenantId: null,
  isLoading: true,
  isAuthenticated: false,
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null }),
  signOut: async () => {},
  resetPassword: async () => ({ error: null }),
});

/**
 * Authentication Provider component
 *
 * Manages authentication state and provides it to child components
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  // Authentication state
  const [authState, setAuthState] = useState<AuthState>({
    session: null,
    user: null,
    userId: null,
    tenantId: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Initialize authentication state
  useEffect(() => {
    // Listen for auth changes
    const { data: { subscription } } = onAuthStateChange((_event, session) => {
      if (session) {
        const user = session.user;
        const tenantId = extractTenantId(session);

        setAuthState({
          session,
          user,
          userId: user.id,
          tenantId,
          isLoading: false,
          isAuthenticated: true,
        });
      } else {
        setAuthState({
          session: null,
          user: null,
          userId: null,
          tenantId: null,
          isLoading: false,
          isAuthenticated: false,
        });
      }
    });

    // Cleanup subscription
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Authentication methods
  const authMethods = {
    signIn: authSignIn,
    signUp: authSignUp,
    signOut: authSignOut,
    resetPassword: authResetPassword,
  };

  // Combine state and methods
  const contextValue: AuthContextType = {
    ...authState,
    ...authMethods,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to use the authentication context
 *
 * @returns Authentication context value
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
